import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  type ColumnDef,
  type PaginationState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { MoreHorizontal, Plus, UserCheck } from 'lucide-react';
import { useState } from 'react';

import type { ListDoctors } from '@virtual-care/client';
import { formatDoctorMid, toAge } from '@virtual-care/isomorphic';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@virtual-care/shadcn';
import {
  ConfirmDialog,
  SearchInput,
  TableRowSort,
  client,
  useAction,
} from '@virtual-care/ui';

import { CountablePageHeader } from '../../components/page-header.tsx';
import { Pagination } from '../../components/pagination.tsx';
import { CreateDoctor } from './create-doctor.tsx';
import { DoctorAvailabilityDialog } from './doctor-availability-dialog.tsx';
import { UpdateDoctorDialog } from './update-doctor';

export default function DoctorsPage() {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchQuery, setSearchQuery] = useState<string | undefined>('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const deleteDoctorMutation = useAction('DELETE /doctors/{id}');

  const columns: ColumnDef<ListDoctors['records'][number]>[] = [
    {
      id: 'mid',
      header: 'MID',
      cell: ({ row }) => {
        return (
          <div className="font-mono">{formatDoctorMid(row.original.mid)}</div>
        );
      },
    },
    {
      id: 'name',
      accessorFn: (row) => `${row.firstName} ${row.lastName}`,
      header: 'Name',
      cell: ({ row }) => {
        const firstName = row.original.firstName || '';
        const lastName = row.original.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim();
        return <div>{fullName || 'N/A'}</div>;
      },
    },
    {
      accessorKey: 'email',
      header: ({ column }) => {
        return 'Email';
      },
      cell: ({ row }) => (
        <div className="lowercase">{row.getValue('email')}</div>
      ),
    },
    {
      accessorKey: 'gender',
      header: 'Gender',
      cell: ({ row }) => {
        const gender = row.getValue('gender') as string;
        return <div className="capitalize">{gender || 'N/A'}</div>;
      },
    },
    {
      accessorKey: 'birthDate',
      header: 'Age',
      cell: ({ row }) => {
        return <div>{toAge(row.original)} years</div>;
      },
    },
    {
      accessorKey: 'phoneNumber',
      enableSorting: false,
      header: 'Phone Number',
      cell: ({ row }) => <div>{row.getValue('phoneNumber') || 'N/A'}</div>,
    },
    {
      id: 'speciality',
      accessorFn: (row) => row.speciality?.name,
      header: 'Speciality',
      cell: ({ row }) => {
        const specialityName = row.original.speciality?.name || 'Unknown';
        return <Badge variant="secondary">{specialityName}</Badge>;
      },
    },
    {
      id: 'actions',
      enableSorting: false,
      cell: ({ row }) => {
        const doctor = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() =>
                  navigator.clipboard.writeText(formatDoctorMid(doctor.mid))
                }
              >
                Copy MID ({formatDoctorMid(doctor.mid)})
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DoctorAvailabilityDialog
                doctorId={doctor.id}
                doctorName={
                  `${doctor.firstName || ''} ${doctor.lastName || ''}`.trim() ||
                  doctor.email
                }
                timezone={doctor.timezone}
                trigger={
                  <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                    View Availability
                  </DropdownMenuItem>
                }
              />
              <UpdateDoctorDialog
                doctor={doctor}
                onSave={() => {
                  dataQuery.refetch();
                }}
                trigger={
                  <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                    Edit doctor
                  </DropdownMenuItem>
                }
              />
              <ConfirmDialog
                title="Delete Doctor"
                description={
                  <span>
                    Are you sure you want to delete doctor{' '}
                    <span className="font-bold">{doctor.email}</span>? This
                    action cannot be undone.
                  </span>
                }
                onConfirm={async () => {
                  await deleteDoctorMutation.mutateAsync({ id: doctor.id });
                  await dataQuery.refetch();
                }}
              >
                <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                  Delete doctor
                </DropdownMenuItem>
              </ConfirmDialog>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const dataQuery = useQuery({
    queryKey: ['doctors', pagination, sorting, searchQuery],
    queryFn: () => {
      return client.request('GET /doctors', {
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        query: searchQuery || undefined,
        sortBy: sorting.length > 0 ? sorting[0].id : undefined,
        sortOrder:
          sorting.length > 0 ? (sorting[0].desc ? 'desc' : 'asc') : undefined,
      });
    },
    placeholderData: keepPreviousData,
  });

  const table = useReactTable({
    data: dataQuery.data?.records || [],
    columns,
    manualPagination: true,
    manualSorting: true,
    rowCount: dataQuery.data?.totalCount,
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      pagination,
    },
  });

  return (
    <div className="w-full">
      <CountablePageHeader
        title="Doctors"
        totalCount={`${dataQuery.data?.totalCount || 0} total doctors`}
        icon={UserCheck}
      >
        <CreateDoctor
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          onSave={() => dataQuery.refetch()}
          trigger={
            <Button>
              <Plus className="mr-2 size-4" />
              Add Doctor
            </Button>
          }
        />
      </CountablePageHeader>

      <div className="mb-4">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          onClear={() => setSearchQuery('')}
        />
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRowSort key={headerGroup.id} headerGroup={headerGroup} />
            ))}
          </TableHeader>
          <TableBody>
            {dataQuery.isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Pagination
        currentPage={table.getState().pagination.pageIndex}
        pageSize={table.getState().pagination.pageSize}
        pageCount={table.getPageCount()}
        totalCount={table.getRowCount()}
        onPageChange={table.setPageIndex}
        onPageSizeChange={table.setPageSize}
      />
    </div>
  );
}
